import {
	requestApiDelete,
	requestApiGet,
	requestApiGetFile,
	requestApiPatch,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { SharedFile } from "../entities/SharedFile";
import type { Star } from "../entities/Star";
import type { Wiki } from "../entities/Wiki";
import type { WikiAttachmentFile } from "../entities/WikiAttachmentFile";
import type { WikiCount } from "../entities/WikiCount";
import type { WikiHistory } from "../entities/WikiHistory";
import type { WikiTag } from "../entities/WikiTag";
import { assert } from "../utils/BacklogUtil";
import type { AddWikiPageParams } from "./params/AddWikiPageParams";
import type { AttachFileToWikiParams } from "./params/AttachFileToWikiParams";
import type { CountWikiPageParams } from "./params/CountWikiPageParams";
import type { DeleteWikiPageParams } from "./params/DeleteWikiPageParams";
import type { GetListOfSharedFilesOnWikiParams } from "./params/GetListOfSharedFilesOnWikiParams";
import type { GetListOfWikiAttachmentsParams } from "./params/GetListOfWikiAttachmentsParams";
import type { GetWikiPageAttachmentParams } from "./params/GetWikiPageAttachmentParams";
import type { GetWikiPageHistoryParams } from "./params/GetWikiPageHistoryParams";
import type { GetWikiPageListParams } from "./params/GetWikiPageListParams";
import type { GetWikiPageParams } from "./params/GetWikiPageParams";
import type { GetWikiPageStarParams } from "./params/GetWikiPageStarParams";
import type { GetWikiPageTagListParams } from "./params/GetWikiPageTagListParams";
import type { LinkSharedFilesToWikiParams } from "./params/LinkSharedFilesToWikiParams";
import type { RemoveLinkToSharedFileFromWikiParams } from "./params/RemoveLinkToSharedFileFromWikiParams";
import type { RemoveWikiAttachmentParams } from "./params/RemoveWikiAttachmentParams";
import type { UpdateWikiPageParams } from "./params/UpdateWikiPageParams";

/**
 * Get Wiki Page List Wikiページ一覧の取得
 *
 * Returns list of Wiki pages.
 */
export function getWikiPageList(
	config: BacklogConfig,
	params: GetWikiPageListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Wiki[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis",
		params,
		context,
	});
}

/**
 * Count Wiki Page Wikiページ数の取得
 *
 * Returns number of Wiki pages.
 */
export function countWikiPage(
	config: BacklogConfig,
	params: CountWikiPageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/count",
		params,
		context,
	});
}

/**
 * Get Wiki Page Tag List Wikiページタグ一覧の取得

 *
 * Returns list of tags that are used in the project.
 */
export function getWikiPageTagList(
	config: BacklogConfig,
	params: GetWikiPageTagListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiTag[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/tags",
		params,
		context,
	});
}

/**
 * Add Wiki Page Wikiページの追加
 *
 * Adds new Wiki page.
 */
export function addWikiPage(
	config: BacklogConfig,
	params: AddWikiPageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Wiki>> {
	assert(params);
	return requestApiPost({
		config,
		path: "wikis",
		params,
		context,
	});
}

/**
 * Get Wiki Page Wikiページ情報の取得
 *
 * Returns information about Wiki page.
 */
export function getWikiPage(
	config: BacklogConfig,
	params: GetWikiPageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Wiki>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/:wikiId",
		params,
		context,
	});
}

/**
 * Update Wiki Page Wikiページ情報の更新
 *
 * Updates information about Wiki page.
 */
export function updateWikiPage(
	config: BacklogConfig,
	params: UpdateWikiPageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Wiki>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "wikis/:wikiId",
		params,
		context,
	});
}

/**
 * Delete Wiki Page Wikiページの削除
 *
 * Deletes Wiki page.
 */
export function deleteWikiPage(
	config: BacklogConfig,
	params: DeleteWikiPageParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Wiki>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "wikis/:wikiId",
		params,
		context,
	});
}

/**
 * Get List of Wiki attachments Wiki添付ファイル一覧の取得
 *
 * Gets list of files attached to Wiki.
 */
export function getListOfWikiAttachments(
	config: BacklogConfig,
	params: GetListOfWikiAttachmentsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiAttachmentFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/:wikiId/attachments",
		params,
		context,
	});
}

/**
 * Attach File to Wiki Wiki添付ファイルの追加
 *
 * Attaches file to Wiki
 */
export function attachFileToWiki(
	config: BacklogConfig,
	params: AttachFileToWikiParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiAttachmentFile[]>> {
	assert(params);
	// TODO: unconfirmed
	return requestApiPost({
		config,
		path: "wikis/:wikiId/attachments",
		params,
		context,
	});
}

/**
 * Get Wiki Page Attachment Wiki添付ファイルのダウンロード
 *
 * Downloads Wiki page’s attachment file.
 */
export function getWikiPageAttachment(
	config: BacklogConfig,
	params: GetWikiPageAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "wikis/:wikiId/attachments/:attachmentId",
		params,
		context,
	});
}

/**
 * Remove Wiki Attachment Wikiページの削除
 *
 * Removes files attached to Wiki.
 */
export function removeWikiAttachment(
	config: BacklogConfig,
	params: RemoveWikiAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiAttachmentFile>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "wikis/:wikiId/attachments/:attachmentId",
		params,
		context,
	});
}

/**
 * Get List of Shared Files on Wiki Wiki共有ファイル一覧の取得
 *
 * Returns the list of Shared Files on Wiki.
 */
export function getListOfSharedFilesOnWiki(
	config: BacklogConfig,
	params: GetListOfSharedFilesOnWikiParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/:wikiId/sharedFiles",
		params,
		context,
	});
}

/**
 * Link Shared Files to Wiki Wikiに共有ファイルをリンク
 *
 * Links Shared Files to Wiki.
 */
export function linkSharedFilesToWiki(
	config: BacklogConfig,
	params: LinkSharedFilesToWikiParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile[]>> {
	assert(params);
	return requestApiPost({
		config,
		path: "wikis/:wikiId/sharedFiles",
		params,
		context,
	});
}

/**
 * Remove Link to Shared File from Wiki Wikiの共有ファイルのリンクを解除
 *
 * Removes link to shared file from Wiki.
 */
export function removeLinkToSharedFileFromWiki(
	config: BacklogConfig,
	params: RemoveLinkToSharedFileFromWikiParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "wikis/:wikiId/sharedFiles/:id",
		params,
		context,
	});
}

/**
 * Get Wiki Page History Wikiページ更新履歴一覧の取得
 *
 * Returns history of Wiki page.
 */
export function getWikiPageHistory(
	config: BacklogConfig,
	params: GetWikiPageHistoryParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<WikiHistory[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/:wikiId/history",
		params,
		context,
	});
}

/**
 * Get Wiki Page Star Wikiページのスター一覧の取得
 *
 * Returns list of stars received on the Wiki page.
 */
export function getWikiPageStar(
	config: BacklogConfig,
	params: GetWikiPageStarParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Star[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "wikis/:wikiId/stars",
		params,
		context,
	});
}
